import { Injectable, inject } from '@angular/core';
import { UpdateAppModalComponent } from '@modals/update-app-modal/update-app-modal.component';
import { ModalController } from '@ionic/angular/standalone';
import { AppInfoService } from './app-info.service';
import { Client, VersionResponse } from '@api/client';
import { firstValueFrom } from 'rxjs';
import { DOWNLOAD_PROMPT_SHOW_PERIOD, URL_DOWNLOAD_APP } from '@constants/app.constants';
import { PlatformService } from './platform.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class UpdateAppService {
  private readonly modalController = inject(ModalController);
  private readonly appInfoService = inject(AppInfoService);
  private readonly client = inject(Client);
  private readonly platformService = inject(PlatformService);
  private readonly localStorage = inject(LocalStorageService);

  public async checkActualAppVersion(): Promise<void> {
    try {
      const actualAppVersions: VersionResponse = await firstValueFrom(this.client.systemGetVersion());
      const lastAppVersion: string = actualAppVersions?.version || actualAppVersions?.last_version; // last_version for the future
      const minimalAppVersion: string = actualAppVersions?.minimal_working_version;

      const localAppVersion: string = await this.appInfoService.getShortAppVersion();

      const updateIsRecommended: boolean = this.isNewer(lastAppVersion, localAppVersion);
      const updateIsRequired: boolean = this.isNewer(minimalAppVersion, localAppVersion);

      if (updateIsRecommended || updateIsRequired) {
        console.warn(`Application version ${localAppVersion} is outdated!\nlast version ${lastAppVersion}\nminimal version ${minimalAppVersion}`);
        await this.openUpdateAppModal(updateIsRequired);
      }
    } catch (error) {
      console.error(`Error checking app version`, error);
    }
  }

  public checkDownloadPromptPeriod(): void {
    if (this.platformService.isSSR || !this.platformService.isAndroidWeb) return;

    const lastPrompt: string = this.localStorage.getItem('lastPrompt');
    const now: number = new Date().getTime();
    const periodExpired: boolean = !lastPrompt || (now - Number(lastPrompt) > DOWNLOAD_PROMPT_SHOW_PERIOD);

    if (periodExpired) {
      this.showDownloadPrompt();
    }
  }

  public showDownloadPrompt(): void {
    if (this.platformService.isSSR || !this.platformService.isAndroidWeb) return;

    setTimeout((): void => {
      const downloadPromptEl: HTMLElement = document.getElementById('downloadPrompt');

      if (downloadPromptEl) {
        downloadPromptEl.style.display = 'block';
      }
    }, 2_000);
  }

  public hideDownloadPrompt(): void {
    if (this.platformService.isSSR) return;

    const now: number = new Date().getTime();
    this.localStorage.setItem('lastPrompt', now.toString());
    const downloadPromptEl: HTMLElement = document.getElementById('downloadPrompt');

    if (downloadPromptEl) {
      downloadPromptEl.style.display = 'none';
    }
  }

  public async downloadApp(): Promise<void> {
    if (this.platformService.isAndroid) {
      document.location.href = URL_DOWNLOAD_APP.ANDROID;
    } else if (this.platformService.isIos) {
      document.location.href = URL_DOWNLOAD_APP.IOS;
    }
  }

  private isNewer(targetAppVersion: string, localAppVersion: string): boolean {
    const targetParts: number[] = targetAppVersion.split('.').map(Number);
    const currentParts: number[] = localAppVersion.split('.').map(Number);

    const length: number = Math.max(targetParts.length, currentParts.length);

    for (let i = 0; i < length; i++) {
      const target: number = targetParts[i] ?? 0;
      const current: number = currentParts[i] ?? 0;

      if (target > current) return true;
      if (target < current) return false;
    }
    return false;
  }

  private async openUpdateAppModal(updateIsRequired: boolean): Promise<void> {
    const opts = {
      component: UpdateAppModalComponent,
      componentProps: {
        updateIsRequired,
      },
      backdropDismiss: !updateIsRequired,
      canDismiss: !updateIsRequired,
    };
    const updateAppModal: HTMLIonModalElement = await this.modalController.create(opts);
    await updateAppModal?.present();
  }
}
